import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SongSearchComponent from "../../components/Playlist/SongSearchComponent";
import SongListManager from "../../components/Playlist/SongListManager";
import PlaylistFormFields from "../../components/Playlist/PlaylistFormFields";
import ThemeSelector from "../../components/Playlist/ThemeSelector";
import { useSongSelection } from "../../components/Playlist/hooks/useSongSelection";
import { usePlaylistValidation } from "../../components/Playlist/hooks/usePlaylistValidation";
import { usePlaylistOperations } from "../../components/Playlist/hooks/usePlaylistOperations";
import { ThemeKey, THEMES } from "../../types/ThemeTypes";
import "./Playlist.css";

const CreatePlaylistPage: React.FC = () => {
    const [playlistName, setPlaylistName] = useState("");
    const [description, setDescription] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [selectedTheme, setSelectedTheme] = useState<ThemeKey>("SING");
    const [error, setError] = useState("");
    const navigate = useNavigate();

    // Use custom hooks
    const songSelection = useSongSelection();
    const validation = usePlaylistValidation();
    const playlistOps = usePlaylistOperations();

    useEffect(() => {
        // Check if user is logged in
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("token");

        console.log("Stored user:", storedUser ? JSON.parse(storedUser) : null);
        console.log("Stored token exists:", !!storedToken);

        if (!storedUser) {
            navigate("/login");
            return;
        }
    }, [navigate]);

    const handleNameChange = (name: string) => {
        setPlaylistName(name);
        validation.validateName(name);
    };

    const handleDescriptionChange = (description: string) => {
        setDescription(description);
        validation.validateDescription(description);
    };

    const handleThemeChange = (theme: ThemeKey) => {
        setSelectedTheme(theme);
    };

    const handleCreatePlaylist = async () => {
        // Validate inputs
        if (!validation.validateAll(playlistName, description)) {
            setError("Please fix the validation errors above");
            return;
        }

        if (songSelection.selectedSongs.length === 0) {
            setError("Please add at least one song to your playlist");
            return;
        }

        setError("");

        try {
            const storedUser = localStorage.getItem("user");
            if (!storedUser) {
                throw new Error("User not found");
            }

            const userData = JSON.parse(storedUser);
            const userId = userData.userId || userData.id || userData.user_id || userData._id;
            if (userId === undefined || userId === null) {
                throw new Error("User ID not found");
            }

            const songIds = songSelection.selectedSongs.map(song => song.melon_song_id);

            const playlistData = await playlistOps.createPlaylist({
                name: playlistName,
                description,
                is_public: isPublic,
                song_ids: songIds,
                userId: userId,
                theme: selectedTheme
            });

            navigate(`/playlist/${playlistData._id}`);
        } catch (error) {
            console.error("Error creating playlist:", error);
            setError(error instanceof Error ? error.message : "Failed to create playlist");
        }
    };

    return (
        <div className="create-playlist-page">
            <div className="create-playlist-container" style={{ backgroundColor: THEMES[selectedTheme].color }}>
                <h2>Create New Playlist</h2>

                {(error || playlistOps.error) && (
                    <div className="error-message">{error || playlistOps.error}</div>
                )}

                <PlaylistFormFields
                    name={playlistName}
                    description={description}
                    isPublic={isPublic}
                    onNameChange={handleNameChange}
                    onDescriptionChange={handleDescriptionChange}
                    onPublicToggle={setIsPublic}
                    nameError={validation.nameError}
                    descriptionError={validation.descriptionError}
                    disabled={playlistOps.isLoading}
                />

                <ThemeSelector
                    selectedTheme={selectedTheme}
                    onThemeChange={handleThemeChange}
                />

                <div className="song-search-section">
                    <h4>add songs</h4>
                    <SongSearchComponent
                        onSongSelect={songSelection.addSong}
                        selectedSongs={songSelection.selectedSongs}
                        placeholder="Search for songs to add"
                        disabled={playlistOps.isLoading}
                        showAddButton={true}
                    />

                    <div className="selected-songs-container">
                        <h4>Selected Songs ({songSelection.selectedSongs.length})</h4>
                        <SongListManager
                            songs={songSelection.enrichedSongs}
                            onRemoveSong={songSelection.removeSong}
                            isEditable={true}
                            emptyMessage="No songs added yet. Use the search bar above to find songs."
                            showNumbers={true}
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        className="cancel-button"
                        onClick={() => {
                            const storedUser = localStorage.getItem("user");
                            if (storedUser) {
                                const userData = JSON.parse(storedUser);
                                navigate(`/user/${userData.dubu_user}`);
                            } else {
                                navigate("/");
                            }
                        }}
                    >
                        X
                    </button>
                    <button
                        className="create-button"
                        onClick={handleCreatePlaylist}
                        disabled={
                            playlistOps.isLoading ||
                            !playlistName.trim() ||
                            songSelection.selectedSongs.length === 0 ||
                            !!validation.nameError ||
                            !!validation.descriptionError
                        }
                    >
                        {playlistOps.isLoading ? "Creating..." : "Create Playlist"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreatePlaylistPage;
